import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ContactCreateEditRequest, IContact } from "../../models/Contact";
import useContactsMutations from "../../data/useContactsMutations";
import { TextInput } from "../shared/Inputs/TextInput";
import PickAddress from "../shared/Addresses/PickAddress";
import { SearchContact } from "./SearchContact";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import useNotification from "../notifications/useNotifications";
import FileUpload from "../shared/Inputs/FileUpload";
import LoadingWheel from "../shared/LoadingWheel";
import { DocumentType, IDocument } from "../../models/Document";
import SelectInput from "../shared/Inputs/SelectInput";
import { CheckboxInput } from "../shared/Inputs/CheckboxInput";
import { AccountType } from "../../models/account/IAccount";
import PossibleContactMatches from "./PossibleContactMatches";
import { canBeRecognised } from "../../utils/contactDuplicates";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

interface ContactSelectorProps {
  onContactSelected: (contact: IContact | null) => void;
  initialContact?: IContact | null;
}

export default function ContactSelector({ onContactSelected, initialContact = null }: ContactSelectorProps) {
  const { t } = useTranslation("accounts");
  const { addNotification } = useNotification();
  const { create: createContact, uploadDoc } = useContactsMutations();
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  const [selectedContact, setSelectedContact] = useState<IContact | null>(initialContact);
  const [toUpload, setToUpload] = useState<any>([]);
  const [uploadIsLoading, setUploadIsLoading] = useState(false);
  // Same email and mobile as someone whose account has different bank details: staff choose.
  const [bankConflict, setBankConflict] = useState<{ message: string; request: ContactCreateEditRequest } | null>(null);

  const updateToUploadFiles = (files: any) => setToUpload({ ...files });

  const { register, handleSubmit, setValue, getValues, watch, reset, } = useForm<ContactCreateEditRequest>({
    defaultValues: {
      fullname: "",
      email: "",
      telephone1: "",
      telephone2: "",
      line1: "",
      line2: "",
      line3: "",
      line4: "",
      locality: "",
      town: "",
      county: "",
      postcode: "",
      contactType: 0,
      notes: "",
      accountName: "",
      bankAccountName: "",
      bankAccountNumber: "",
      bankSortCode: "",
      createAccount: false,
    },
  });

  const handleCreateContact = (data: ContactCreateEditRequest) => submitContact(data, true);

  const submitContact = (data: ContactCreateEditRequest, reuseExisting: boolean) => {

    if (!data.fullname || data.fullname.trim() === "") {
      addNotification({
        variant: "error",
        primaryText: t("Please add at least a name before creating a contact."),
      });
      return;
    }

    if (!canBeRecognised(data.email, data.telephone1, data.telephone2)) {
      addNotification({
        variant: "error",
        primaryText: t("Add an email address or a phone number, so this person is recognised next time."),
      });
      return;
    }

    setUploadIsLoading(true);
    data.createAccountQuery = true; // Always create an account when creating a contact here
    data.reuseExistingQuery = reuseExisting; // Someone we already have: use them instead
    data.accountType = data.createAccount ? AccountType.Manual : AccountType.Auto;

    createContact.mutate(data, {
      onSuccess: (res: any) => {
        // Nothing was created: they exist, but their account would pay a different bank account.
        if (!res.success && res.data) {
          setUploadIsLoading(false);
          setBankConflict({ message: res.message, request: data });
          return;
        }

        if (!res.success) {
          setUploadIsLoading(false);
          addNotification({ variant: "error", primaryText: t("Contact not created"), secondaryText: res.message });
          return;
        }

        setSelectedContact(res.data);
        onContactSelected(res.data);
        // Says whether a new contact was made or an existing person was used.
        addNotification({ variant: "success", primaryText: t("Contact selected"), secondaryText: res.message });

        handleUploadDocs(res.data.id);
      },
      onError: (error) => {
        console.log(error);
        setUploadIsLoading(false);
      },
    });
  };

  const handleUploadDocs = (contactId: string) => {
    //attach files to http request
    setUploadIsLoading(true);

    const formData = new FormData();

    for (let i = 0; i < Object.keys(toUpload).length; i++) {
      formData.append(DocumentType[DocumentType.Identity].toString(), toUpload[i]);
    }

    if (Object.keys(toUpload).length === 0) {
      setIsCreatingContact(false);
      setUploadIsLoading(false);
      return;
    }

    uploadDoc.mutate({ data: formData, contactId: contactId }, {
      onSuccess: () => {
        setToUpload([]);
      },
      onError: (error) => {
        console.log(error);
      },
      onSettled: () => {
        setIsCreatingContact(false);
        setUploadIsLoading(false);
      }
    })
  }

  useEffect(() => {
    // console.log("selectedContact changed", selectedContact);
    if (!selectedContact || selectedContact === null) {

      reset({
        fullname: "",
        email: "",
        telephone1: "",
        telephone2: "",
        line1: "",
        line2: "",
        line3: "",
        line4: "",
        locality: "",
        town: "",
        county: "",
        postcode: "",
        contactType: 0,
        notes: "",
        accountName: "",
        bankAccountName: "",
        bankAccountNumber: "",
        bankSortCode: "",
        createAccount: false,
      });

    } else {
      reset({ ...selectedContact });
    }
  }, [selectedContact]);

  const watchFullName = watch("fullname");
  const watchCreateAccount = watch("createAccount");
  const watchEmail = watch("email");
  const watchTelephone1 = watch("telephone1");
  const watchTelephone2 = watch("telephone2");

  useEffect(() => {
    setValue("accountName", watchFullName ? watchFullName : "");
  }, [watchFullName]);

  return (
    <div>
      <ConfirmModal
        open={!!bankConflict}
        setOpen={(open: boolean) => !open && setBankConflict(null)}
        title={t("Different bank details")}
        message={`${bankConflict?.message ?? ""} ${t("To use them, pick them from the box above and update their account's bank details afterwards. Or create a separate contact and account with the bank details you typed.")}`}
        confirmButtonText={t("Create separate contact")}
        onConfirm={() => {
          const request = bankConflict?.request;
          setBankConflict(null);
          if (request) submitContact(request, false);
        }}
        isLoading={createContact.isLoading}
        Icon={<ExclamationTriangleIcon className="h-6 w-6 text-amber-600" aria-hidden="true" />}
        iconBg="bg-amber-100"
      />
      {!isCreatingContact ? (
        <>
          <SearchContact label={t("Select Contact")}
            selectedContact={selectedContact}
            onContactSelect={(contact) => {
              setSelectedContact(contact);
              onContactSelected(contact);
            }} />

          {selectedContact && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput
                label={t("Full Name")}
                register={register}
                registerName="fullname"
                disabled={true}
              />
              <TextInput
                label={t("Email")}
                register={register}
                registerName="email"
                disabled={true}
              />
              <TextInput
                label={t("Telephone 1")}
                register={register}
                registerName="telephone1"
                disabled={true}
              />
              <TextInput
                label={t("Telephone 2")}
                register={register}
                registerName="telephone2"
                disabled={true}
              />
              <TextInput
                label={t("Postcode")}
                register={register}
                registerName="postcode"
                disabled={true}
              />
              <TextInput
                label={t("Address Line 1")}
                register={register}
                registerName="line1"
                disabled={true}
              />
            </div>
          )}

          <div className="mt-2 flex gap-2">
            <ButtonInput
              label={t("Create Contact")}
              isSubmit={false}
              onClick={() => {
                setIsCreatingContact(true);
                setSelectedContact(null);
                onContactSelected(null);
              }}
              classes=""
            />
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput
              label={t("Full Name")}
              register={register}
              registerName="fullname"
              required
            />
            <TextInput
              label={t("Email")}
              register={register}
              registerName="email"
            />
            <TextInput
              label={t("Telephone 1")}
              register={register}
              registerName="telephone1"
            />
            <TextInput
              label={t("Telephone 2")}
              register={register}
              registerName="telephone2"
            />
          </div>

          <PossibleContactMatches
            email={watchEmail}
            phone={watchTelephone1 || watchTelephone2}
            onUse={(match) => {
              // Same shape as a search pick, so it goes through the same path.
              setSelectedContact(match);
              onContactSelected(match);
              setIsCreatingContact(false);
            }}
          />

          <PickAddress
            register={register}
            setValue={setValue}
            watch={watch}
          />

          <div className="flex flex-col gap-4">
            <h1 className="text-lg font-semibold col-span-1 md:col-span-3">{t("Account Details")}</h1>

            <CheckboxInput
              label={t("Create Regulars Account?")}
              register={register}
              registerName="createAccount"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {watchCreateAccount ? (
                <TextInput
                  label={t("Account Name")}
                  register={register}
                  registerName="accountName"
                />
              ) : <div />}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">

            <h1 className="text-lg font-semibold col-span-1 md:col-span-3">{t("Payment Details")}</h1>
            <TextInput
              label={t("Bank Account Name")}
              register={register}
              registerName="bankAccountName"
            />
            <TextInput
              label={t("Bank Account Number")}
              register={register}
              registerName="bankAccountNumber"
            />
            <TextInput
              label={t("Bank Sort Code")}
              register={register}
              registerName="bankSortCode"
            />
          </div>

          <div>
            {/* ID docs */}
            {uploadIsLoading ? (
              <div className="flex items-center py-[10vh]">
                <LoadingWheel />
              </div>
            ) : (
              <FileUpload
                accept=".jpg,.png,.jpeg,.pdf"
                label={t("Upload ID Documents")}
                desc={t("Optionally upload identity documents for this contact. You can upload multiple files.")}
                multiple
                updateFilesCb={updateToUploadFiles}
              />
            )}
          </div>

          <div className="flex gap-2">
            <ButtonInput
              label={t("Save")}
              isSubmit={false}
              onClick={handleSubmit(handleCreateContact)}
              classes=""
              isLoading={createContact.isLoading}
            />
            <ButtonInput
              label={t("Cancel")}
              isSubmit={false}
              onClick={() => setIsCreatingContact(false)}
              classes=""
            />
          </div>
        </div>
      )}
    </div>
  );
} 